<template>
  <product-card v-bind="forwarded">
    <template #close="{ quickshopMode }">
      <base-button>
        <vf-icon
          v-if="quickshopMode === 'off'"
          class="m-2 right-0 top-0 absolute z-2"
          name="close"
          size="md"
          @click="$emit('remove')"
        />
      </base-button>
    </template>
    <template #shopNow="{ quickshopMode, navigateToPDP }" v-if="quickshopMode === 'off'">
      <hr class="c-grey-70 mt-2">
      <vf-button
        class="mt-4 self-start relative"
        data-test-id="product-shop-now"
        size="tiny"
        variant="tertiary"
        @click="navigateToPDP()"
      >
        {{ $t.shopNow }}
      </vf-button>
    </template>
    <template
      v-if="$feature.tempShowCompareOnPdpAndPlp
        && productCardConfig.comparePlps?.some((path) => route.path.includes(path))"
      #compareCheckbox="{ swatchModel }"
    >
      <span v-if="swatchModel" class="hidden">{{ updateColor(product.styleId, swatchModel) }}</span>
      <vf-checkbox
        v-if="compareData?.some(compareItem => product.id.includes(compareItem.productId))"
        v-model="isChecked"
        class="pt-4 z-2"
        :disabled="compareItems.length >= 4 && !isChecked"
      >
        {{ $t.compare }}
      </vf-checkbox>
    </template>
  </product-card>
</template>

<script lang="ts" setup>
import type { Emits, Props } from '#commerce/components/product/card/Card.vue'
import ProductCard from '#commerce/components/product/card/Card.vue'

const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const forwarded = useForwardPropsEmits(props, emits)

const compareData = await useApi().internal.getCompare().data

const { $feature, $gtm } = useNuxtApp()
const isChecked = ref(false)
const { compareItems } = storeToRefs(useCompareStore())
const { addCompareItem, removeCompareItem } = useCompareStore()
const productCardConfig = useAppConfig().components.product.card
const compareDialogConfig = useAppConfig().components.dialog.compare
const color = ref('')
const route = useRoute()

const getProductByColor = (product) =>
  product.attributes.find((attr) => attr.type === 'color')?.options.find((option) => option.id === color.value)

const updateColor = (styleId, swatch) => {
  color.value = swatch

  // update color on compareItems, if it exists
  const productToUpdate = compareItems.value.find((product) => product.styleId === styleId)
  if (!productToUpdate) return

  const { gallery, id } = getProductByColor(productToUpdate)
  productToUpdate.gallery = gallery
  productToUpdate.id = id
}

watch(() => isChecked.value, () => {
  if (isChecked.value) {
    const { gallery, id } = getProductByColor(props.product)
    addCompareItem({ ...props.product, gallery, id })
    $gtm.push('plpPage.onSelectCompare', props.breadcrumbs?.[props.breadcrumbs.length - 1]?.label)
  }
  else {
    if (compareItems.value.some(({ styleId }) => styleId === props.product.styleId))
      $gtm.push('plpPage.onRemoveCompare', props.breadcrumbs?.[props.breadcrumbs.length - 1]?.label)
    removeCompareItem(props.product.styleId)
  }
})

watch(() => compareItems.value.map((item) => item.styleId), (styleIds) => {
  isChecked.value = styleIds.includes(props.product.styleId)
}, { immediate: true })

if ($feature.tempShowCompareOnPdpAndPlp) {
  productCardConfig.comparePlps = ['backpacks-224451', 'womens-backpacks-268801', 'mens-backpacks-298772']
  compareDialogConfig.compareKeyOrder = [
    'keyFeatures',
    'material',
    'volumeCapacity',
    'weight',
    'dimensions',
    'laptopDimension'
  ]
}
</script>
